import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUI } from "@/context/UIContext";
import api, { formatApiError } from "@/lib/api";
import { track, getSource } from "@/lib/track";
import { toast } from "sonner";
import { btnPrimary, inputCls, labelCls, overline } from "@/components/kit";
import { CalendarClock } from "lucide-react";

const SLOTS = ["Matin (9h – 12h)", "Après-midi (14h – 17h)", "Fin de journée (17h – 19h)"];

export default function CallbackModal() {
  const { callbackOpen, setCallbackOpen } = useUI();
  const [form, setForm] = useState({ name: "", phone: "", company: "", date: "", slot: "" });
  const [loading, setLoading] = useState(false);

  // Date minimale : demain (jours ouvrés privilégiés, choix libre)
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Merci d'indiquer votre nom et votre téléphone.");
      return;
    }
    setLoading(true);
    try {
      const callback_slot = form.date && form.slot ? `${form.date} · ${form.slot}` : undefined;
      await api.post("/submissions", { type: "rappel", name: form.name, phone: form.phone, company: form.company, callback_slot, source: getSource() });
      track("form_submit");
      toast.success(callback_slot ? `Créneau réservé : ${callback_slot}. À très vite !` : "Merci ! Nous vous rappelons très vite.");
      setForm({ name: "", phone: "", company: "", date: "", slot: "" });
      setCallbackOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={callbackOpen} onOpenChange={setCallbackOpen}>
      <DialogContent className="border-ink/10 bg-white text-ink sm:max-w-md" data-testid="callback-modal">
        <DialogHeader>
          <span className={overline}>Échange de 15 minutes</span>
          <DialogTitle className="font-serif text-3xl font-medium">Parlons de votre projet</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink/60">Choisissez votre créneau : un chef de projet vous appelle pour un premier échange de 15 minutes, sans engagement.</p>
        <form onSubmit={submit} className="mt-2 space-y-5">
          <div>
            <label htmlFor="callback-name" className={labelCls}>Nom complet *</label>
            <input id="callback-name" required className={inputCls} data-testid="callback-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jean Dupont" />
          </div>
          <div>
            <label htmlFor="callback-phone" className={labelCls}>Téléphone *</label>
            <input id="callback-phone" type="tel" required className={inputCls} data-testid="callback-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" />
          </div>
          <div>
            <label htmlFor="callback-company" className={labelCls}>Société</label>
            <input id="callback-company" className={inputCls} data-testid="callback-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Votre entreprise" />
          </div>
          <div>
            <label htmlFor="callback-date" className={labelCls}>Date souhaitée</label>
            <input id="callback-date" type="date" min={minDate} className={inputCls} data-testid="callback-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <span className={labelCls}>Créneau préféré</span>
            <div className="flex flex-wrap gap-2" data-testid="callback-slots">
              {SLOTS.map((s) => {
                const active = form.slot === s;
                return (
                  <button
                    type="button"
                    key={s}
                    aria-pressed={active}
                    data-testid={`callback-slot-${s.split(" ")[0].toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                    onClick={() => setForm({ ...form, slot: active ? "" : s })}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-lime bg-lime text-ink" : "border-ink/15 bg-white text-ink/60 hover:border-lime-dim hover:text-ink"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <button type="submit" disabled={loading} className={`${btnPrimary} w-full disabled:opacity-60`} data-testid="callback-submit">
            <CalendarClock size={16} />
            {loading ? "Envoi…" : "Réserver mon échange"}
          </button>
          <p className="text-xs leading-relaxed text-ink/45">
            Vos informations servent uniquement à traiter votre demande.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
